/**
 * Mock Data Audit diagnostic card.
 *
 * Identifies mock/sandbox data by LINKAGE (source field, institution/connection
 * provenance), not by name or balance. Three provenance classes:
 *   1. source=="mock" seed-script accounts
 *   2. Bridge sandbox demo bank accounts
 *   3. TrueLayer sandbox mock bank accounts
 *
 * Accounts with active connections are EXCLUDED from purge and badged.
 * Accounts with recent transaction activity are QUARANTINED (require
 * individual confirmation, never included in Purge All).
 * Per-account transaction counts and last_transaction_date shown.
 *
 * Bank account record corrections: IBAN/id-prefix detection of mismatched fields.
 *
 * Tag Bridge sandbox data: operator-confirmed retroactive tagging for
 * source=="bridge" rows that carry no provenance tag the classifier above
 * recognizes. Preview-then-apply; apply stamps source="mock".
 */

import { useState } from 'react';
import { Loader2, Search, Trash2, CheckCircle2, AlertTriangle, Shield, Clock, ArrowRight, Wrench, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { mockDataAuditAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatDate(isoStr) {
  if (!isoStr) return '--';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

var PROVENANCE_COLORS = {
  mock: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  bridge_sandbox: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  truelayer_sandbox: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
};

function AccountGroup({ accounts, title, showReason }) {
  if (!accounts || accounts.length === 0) return null;

  return (
    <div>
      <p className="text-sm font-semibold mb-1">{title} ({accounts.length})</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-1.5 pr-3 font-medium">Account</th>
              <th className="py-1.5 pr-3 font-medium">Entity</th>
              <th className="py-1.5 pr-3 font-medium">Bank</th>
              <th className="py-1.5 pr-3 font-medium">Source</th>
              <th className="py-1.5 pr-3 font-medium">Txns</th>
              <th className="py-1.5 pr-3 font-medium">Last Tx</th>
              <th className="py-1.5 pr-3 font-medium">Connection</th>
              <th className="py-1.5 pr-3 font-medium">Created</th>
              {showReason && <th className="py-1.5 pr-3 font-medium">Evidence</th>}
            </tr>
          </thead>
          <tbody>
            {accounts.map(function (acc) {
              var connLabel = acc.connection_id
                ? (acc.connection_provider || '') + ': ' + acc.connection_id
                : 'none (verified)';
              var connStatus = acc.connection_status || 'none';
              return (
                <tr key={acc.id} className={
                  'border-b border-border/50' +
                  (acc.excluded_from_purge ? ' bg-green-50 dark:bg-green-950/20' : '')
                }>
                  <td className="py-1.5 pr-3">
                    {acc.account_name}
                    {acc.excluded_from_purge && (
                      <Badge className="ml-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-[10px]">
                        excluded
                      </Badge>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-xs">{acc.entity_name}</td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground">{acc.bank_name}</td>
                  <td className="py-1.5 pr-3">
                    <Badge className={PROVENANCE_COLORS[acc.provenance] || ''}>
                      {acc.provenance}
                    </Badge>
                  </td>
                  <td className="py-1.5 pr-3 text-xs font-mono text-right">
                    {acc.transaction_count != null ? acc.transaction_count : '--'}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground">
                    {acc.last_transaction_date ? String(acc.last_transaction_date).slice(0, 10) : '--'}
                  </td>
                  <td className="py-1.5 pr-3 text-xs font-mono text-muted-foreground truncate max-w-[160px]">
                    {connStatus === 'active' ? (
                      <span className="text-green-700 dark:text-green-400 font-medium">
                        {connLabel}
                      </span>
                    ) : connLabel}
                  </td>
                  <td className="py-1.5 pr-3 text-xs text-muted-foreground">
                    {formatDate(acc.created_at)}
                  </td>
                  {showReason && (
                    <td className="py-1.5 pr-3 text-xs text-muted-foreground max-w-[280px]">
                      {acc.exclusion_reason || '--'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MockDataAuditPanel() {
  var [loading, setLoading] = useState(false);
  var [purging, setPurging] = useState(false);
  var [data, setData] = useState(null);
  // Corrections state
  var [correctionsLoading, setCorrectionsLoading] = useState(false);
  var [correctionsApplying, setCorrectionsApplying] = useState(false);
  var [correctionsResult, setCorrectionsResult] = useState(null);
  var [correctionsApplied, setCorrectionsApplied] = useState(false);
  // Bridge sandbox tagging state
  var [bridgeTagLoading, setBridgeTagLoading] = useState(false);
  var [bridgeTagApplying, setBridgeTagApplying] = useState(false);
  var [bridgeTagResult, setBridgeTagResult] = useState(null);
  var [bridgeTagApplied, setBridgeTagApplied] = useState(null);

  var loadAudit = async function () {
    setLoading(true);
    setData(null);
    try {
      var res = await mockDataAuditAPI.audit();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var handlePurge = async function () {
    setPurging(true);
    try {
      var res = await mockDataAuditAPI.purge(false);
      var result = res.data;
      var msg = 'Purged ' + result.accounts_deleted + ' account(s) and ' +
        result.transactions_deleted + ' transaction(s)';
      if (result.excluded_count > 0) {
        msg += ' (' + result.excluded_count + ' excluded, active connections)';
      }
      if (result.quarantined_count > 0) {
        msg += ', ' + result.quarantined_count + ' quarantined (recent activity)';
      }
      if (result.manifest_id) {
        msg += '. Manifest: ' + result.manifest_id;
      }
      toast.success(msg);
      await loadAudit();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPurging(false);
    }
  };

  var handleCorrectionsPreview = async function () {
    setCorrectionsLoading(true);
    setCorrectionsResult(null);
    setCorrectionsApplied(false);
    try {
      var res = await mockDataAuditAPI.correctionsPreview();
      setCorrectionsResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCorrectionsLoading(false);
    }
  };

  var handleCorrectionsApply = async function () {
    setCorrectionsApplying(true);
    try {
      var res = await mockDataAuditAPI.correctionsApply();
      toast.success('Applied ' + res.data.corrections_applied + ' correction(s)');
      setCorrectionsApplied(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCorrectionsApplying(false);
    }
  };

  var handleBridgeTagPreview = async function () {
    setBridgeTagLoading(true);
    setBridgeTagResult(null);
    setBridgeTagApplied(null);
    try {
      var res = await mockDataAuditAPI.bridgeSandboxTagPreview();
      setBridgeTagResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBridgeTagLoading(false);
    }
  };

  var handleBridgeTagApply = async function () {
    setBridgeTagApplying(true);
    try {
      var res = await mockDataAuditAPI.bridgeSandboxTagApply();
      var result = res.data;
      toast.success(
        'Tagged ' + result.accounts_tagged + ' account(s) and ' +
        result.transactions_tagged + ' transaction(s) as mock. Manifest: ' + result.manifest_id
      );
      setBridgeTagApplied(result);
      await handleBridgeTagPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBridgeTagApplying(false);
    }
  };

  var totalAccounts = data ? data.total_mock_accounts : 0;
  var totalExcluded = data ? (data.total_excluded || 0) : 0;
  var totalQuarantined = data ? (data.total_quarantined || 0) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Mock Data Audit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Identifies mock/sandbox data by provenance linkage: seed-script mock accounts,
          Bridge sandbox demo bank accounts, and TrueLayer sandbox accounts. Selection
          is by source field and institution name, never by balance or counterparty name.
          Accounts with active connections are excluded. Accounts with recent transaction
          activity are quarantined (require individual confirmation).
        </p>

        <div className="flex gap-2">
          <Button onClick={loadAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {data && (totalAccounts > 0 || data.orphan_mock_transaction_count > 0) && (
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={purging || loading}
            >
              {purging ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Purge All ({totalAccounts} accounts, {data.linked_transaction_count + data.orphan_mock_transaction_count} txns)
            </Button>
          )}
        </div>

        {data && (
          <div className="space-y-4">
            {data.error && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span>{data.error}</span>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center gap-2 text-sm">
              {totalAccounts === 0 && totalExcluded === 0 && totalQuarantined === 0 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">No mock/sandbox data found</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span>
                    {totalAccounts} purgeable account{totalAccounts !== 1 ? 's' : ''},{' '}
                    {data.linked_transaction_count} linked transaction{data.linked_transaction_count !== 1 ? 's' : ''}
                    {data.orphan_mock_transaction_count > 0 && (
                      <>, {data.orphan_mock_transaction_count} orphan mock txn{data.orphan_mock_transaction_count !== 1 ? 's' : ''}</>
                    )}
                    {totalExcluded > 0 && (
                      <>, {totalExcluded} excluded (active connections)</>
                    )}
                    {totalQuarantined > 0 && (
                      <>, {totalQuarantined} quarantined (recent activity)</>
                    )}
                  </span>
                </>
              )}
            </div>

            {/* Excluded accounts (active connections) */}
            {data.excluded_accounts && data.excluded_accounts.length > 0 && (
              <div className="border border-green-200 dark:border-green-800 rounded p-3 bg-green-50/50 dark:bg-green-950/20">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-800 dark:text-green-400">
                    Protected by Active Connections ({data.excluded_accounts.length})
                  </span>
                </div>
                <AccountGroup
                  accounts={data.excluded_accounts}
                  title="Excluded from Purge"
                  showReason
                />
              </div>
            )}

            {/* Quarantined accounts (recent activity) */}
            {data.quarantined_accounts && data.quarantined_accounts.length > 0 && (
              <div className="border border-amber-200 dark:border-amber-800 rounded p-3 bg-amber-50/50 dark:bg-amber-950/20">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                    Quarantined: Recent Activity ({data.quarantined_accounts.length})
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  These accounts have transactions within the last {data.recency_threshold_days || 35} days.
                  They are NOT included in "Purge All" and require individual confirmation.
                </p>
                <AccountGroup
                  accounts={data.quarantined_accounts}
                  title="Requires Individual Confirmation"
                  showReason
                />
              </div>
            )}

            {/* Account groups */}
            <AccountGroup
              accounts={data.mock_accounts}
              title="Seed-Script Mock Accounts"
            />
            <AccountGroup
              accounts={data.bridge_sandbox_accounts}
              title="Bridge Sandbox Accounts"
            />
            <AccountGroup
              accounts={data.truelayer_sandbox_accounts}
              title="TrueLayer Sandbox Accounts"
            />

            {/* Write-path indicators */}
            {data.write_path_indicators && data.write_path_indicators.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-1">Write-Path Analysis (sample)</p>
                <div className="space-y-1">
                  {data.write_path_indicators.map(function (wp, i) {
                    return (
                      <div key={i} className="text-xs text-muted-foreground flex gap-3">
                        <span className="font-mono">{wp.account_id?.slice(0, 12)}...</span>
                        <Badge variant="outline" className="text-[10px]">{wp.verdict}</Badge>
                        <span>created: {formatDate(wp.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Transaction samples */}
            {data.transaction_samples && data.transaction_samples.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-1">Transaction Samples (most recent)</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2 font-medium">Date</th>
                        <th className="py-1 pr-2 font-medium">Description</th>
                        <th className="py-1 pr-2 font-medium">Counterparty</th>
                        <th className="py-1 pr-2 font-medium text-right">Amount</th>
                        <th className="py-1 pr-2 font-medium">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.transaction_samples.map(function (tx, i) {
                        return (
                          <tr key={i} className="border-b border-border/50">
                            <td className="py-1 pr-2 text-muted-foreground">{tx.date?.slice(0, 10)}</td>
                            <td className="py-1 pr-2 truncate max-w-[200px]">{tx.description}</td>
                            <td className="py-1 pr-2">{tx.counterparty || '--'}</td>
                            <td className="py-1 pr-2 text-right font-mono">
                              {tx.amount != null ? Number(tx.amount).toFixed(2) : '--'} {tx.currency}
                            </td>
                            <td className="py-1 pr-2">
                              <Badge variant="outline" className="text-[10px]">{tx.source || 'none'}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Bank Account Record Corrections ── */}
        <div className="border border-slate-200 dark:border-slate-700 rounded p-3 bg-slate-50/50 dark:bg-slate-950/20">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-400">
              Bank Account Record Corrections
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Detects bank_name / source mismatches via IBAN prefix, account ID prefix,
            and revolut_connection field. Preview-then-apply.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCorrectionsPreview}
              disabled={correctionsLoading}
            >
              {correctionsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Search className="h-4 w-4 mr-1" />
              )}
              Preview Corrections
            </Button>
            {correctionsResult && correctionsResult.corrections_needed > 0 && !correctionsApplied && (
              <Button
                size="sm"
                onClick={handleCorrectionsApply}
                disabled={correctionsApplying}
              >
                {correctionsApplying ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Wrench className="h-4 w-4 mr-1" />
                )}
                Apply {correctionsResult.corrections_needed} Correction(s)
              </Button>
            )}
          </div>

          {correctionsResult && (
            <div className="mt-3 text-xs">
              <p className="text-muted-foreground mb-1">
                Scanned {correctionsResult.total_accounts_scanned} accounts,{' '}
                {correctionsResult.corrections_needed} correction(s) needed
              </p>
              {correctionsResult.corrections && correctionsResult.corrections.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2 font-medium">Account</th>
                        <th className="py-1 pr-2 font-medium">Entity</th>
                        <th className="py-1 pr-2 font-medium">Before</th>
                        <th className="py-1 pr-2 font-medium"></th>
                        <th className="py-1 pr-2 font-medium">After</th>
                        <th className="py-1 pr-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {correctionsResult.corrections.map(function (c) {
                        return (
                          <tr key={c.account_id} className="border-b border-border/50">
                            <td className="py-1 pr-2">{c.account_name}</td>
                            <td className="py-1 pr-2 text-muted-foreground">{c.entity_name}</td>
                            <td className="py-1 pr-2 font-mono text-red-600">
                              {c.before.bank_name} / {c.before.source}
                            </td>
                            <td className="py-1 pr-1">
                              <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            </td>
                            <td className="py-1 pr-2 font-mono text-green-600">
                              {c.after.bank_name} / {c.after.source}
                            </td>
                            <td className="py-1 pr-2 text-muted-foreground">{c.detection_reason}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {correctionsApplied && (
                <div className="flex items-center gap-2 mt-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Corrections applied successfully</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Tag Bridge Sandbox Data ── */}
        <div className="border border-purple-200 dark:border-purple-800 rounded p-3 bg-purple-50/50 dark:bg-purple-950/20">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-800 dark:text-purple-400">
              Tag Bridge Sandbox Data
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Operator-confirmed: this deployment's Bridge feed has only ever connected to
            Bridge's sandbox bank, so source=="bridge" rows are mock by origin but carry no
            provenance tag. Selection is positive only: transactions with source=="bridge",
            and bank_accounts whose id is actually referenced by those transactions.
            Connections on an allowed production bank are excluded. Preview-then-apply;
            apply stamps source="mock" (the tag the audit above already reads) and snapshots
            before-state to a purge manifest.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBridgeTagPreview}
              disabled={bridgeTagLoading}
            >
              {bridgeTagLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Search className="h-4 w-4 mr-1" />
              )}
              Preview
            </Button>
            {bridgeTagResult && (bridgeTagResult.accounts_count > 0 || bridgeTagResult.transactions_to_tag_count > 0) && (
              <Button
                size="sm"
                onClick={handleBridgeTagApply}
                disabled={bridgeTagApplying}
              >
                {bridgeTagApplying ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Tag className="h-4 w-4 mr-1" />
                )}
                Tag {bridgeTagResult.accounts_count} account(s), {bridgeTagResult.transactions_to_tag_count} txn(s)
              </Button>
            )}
          </div>

          {bridgeTagResult && (
            <div className="mt-3 text-xs">
              <p className="text-muted-foreground mb-1">
                {bridgeTagResult.accounts_count} account(s), {bridgeTagResult.transactions_to_tag_count} transaction(s) to tag
                {bridgeTagResult.protected_accounts && bridgeTagResult.protected_accounts.length > 0 && (
                  <>, {bridgeTagResult.protected_accounts.length} protected (allowed-bank connection)</>
                )}
              </p>
              {bridgeTagResult.accounts_to_tag && bridgeTagResult.accounts_to_tag.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2 font-medium">Account</th>
                        <th className="py-1 pr-2 font-medium">Entity</th>
                        <th className="py-1 pr-2 font-medium">Bank</th>
                        <th className="py-1 pr-2 font-medium">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bridgeTagResult.accounts_to_tag.map(function (a) {
                        return (
                          <tr key={a.id} className="border-b border-border/50">
                            <td className="py-1 pr-2">{a.account_name}</td>
                            <td className="py-1 pr-2 text-muted-foreground">{a.entity_name}</td>
                            <td className="py-1 pr-2 text-muted-foreground">{a.bank_name}</td>
                            <td className="py-1 pr-2">
                              <Badge variant="outline" className="text-[10px]">{a.source}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {bridgeTagApplied && (
                <div className="flex items-center gap-2 mt-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>
                    Tagged {bridgeTagApplied.accounts_tagged} account(s), {bridgeTagApplied.transactions_tagged} transaction(s).
                    Manifest: {bridgeTagApplied.manifest_id}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
